import { Box, CircularProgress, Stack, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/system';
import { LocalizationProvider, DateRange } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TZDate } from '@repo/tzdate';
import { Dayjs } from 'dayjs';
import { convertDateRangeToTZDate } from 'src/utils/dateRangeUtils';
import { useCallback, useMemo } from 'react';
import StaticDateRangePicker from 'src/components/common/date-range-picker/StaticDateRangePicker';
import { getLocaleNumberFormatNoDecimals, useLocale } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { groupBy } from 'lodash-es';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { GuestConfig } from 'src/hooks/domain/booking/useAccommodationSearchQuantities';

type Props = {
    activities: ActivityInCalendar[];
    value: DateRange<TZDate>;
    onChange: (value: DateRange<TZDate>, context?: any) => void;
    onChangeMonth: (value: Dayjs | TZDate) => void;
    isLoading?: boolean;
    guests: GuestConfig[];
    expectPrice?: boolean;
    minDate?: TZDate;
};

export type ActivityInCalendar = {
    id: string;
    start: TZDate;
    firstTicketOptionPrice: number | null;
    capacity: number;
    personsPerUnit: number | null;
};

export default function Calendar(props: Props) {
    const { guests, activities, value, onChange, onChangeMonth, isLoading, expectPrice } = props;
    const { locale, dayjsLocale } = useLocale();
    const customizations = useCustomizations();
    const theme = useTheme();
    const config = useConfigurations();
    const showWeekNumbers = config.showWeekNumbers ?? false;

    const calendarAvailability = useCalendarAvailability(activities, guests);

    const { calendarSx, calendarDaySx } = useCalendarSx();

    const onChangeDateRange = useCallback(
        (dateRange: DateRange<Dayjs | TZDate>, _context?: any) => {
            const convertedRange = convertDateRangeToTZDate(dateRange);
            const [date1, date2] = convertedRange;

            if (date1 && date2 && date1.isSame(date2)) {
                onChange([date1, null] as DateRange<TZDate>);
                return;
            }
            const isInvalidRange =
                date1 &&
                date2 &&
                new Array(Math.abs(date1.diff(date2, 'days'))).fill(null).some((_, i) => {
                    const firstDate = date1!.isBefore(date2!) ? date1 : date2;
                    const date = firstDate!.add(i, 'days');

                    const d = !calendarAvailability.has(date.format('YYYY-MM-DD'));
                    return d;
                });
            if (isInvalidRange) {
                onChange([date1, null] as DateRange<TZDate>);

                return;
            }
            onChange(convertedRange);
        },
        [calendarAvailability, onChange],
    );

    const renderCustomDay = useCallback(
        (date: TZDate) => {
            const availability = calendarAvailability.get(date.format('YYYY-MM-DD'));

            return (
                <Stack gap={1}>
                    <Box sx={{ lineHeight: 1, fontSize: 16 }}>{date.format('DD')}</Box>
                    <Box
                        sx={{
                            lineHeight: 1,
                            fontSize: 12,
                            [`@container (width < ${360 - customizations.baseUnit * 4}px)`]: {
                                display: 'none',
                            },
                        }}
                    >
                        {availability && getLowestPriceFromAvailabilities(availability, locale)}
                    </Box>
                </Stack>
            );
        },
        [calendarAvailability, customizations.baseUnit, locale],
    );

    return (
        <Stack direction="row" justifyContent="center" width="100%" position="relative">
            {isLoading && (
                <Stack
                    position="absolute"
                    zIndex={10}
                    left={0}
                    right={0}
                    top={0}
                    bottom={0}
                    width="100%"
                    height="100%"
                    bgcolor={theme.palette.common.white}
                    justifyContent="center"
                    alignItems="center"
                    border={`1px solid ${theme.palette.grey[200]}`}
                    borderRadius={customizations.borderRadius + 'px'}
                >
                    <CircularProgress />
                </Stack>
            )}
            <Stack
                borderRadius={customizations.borderRadius + 'px'}
                border={`1px solid ${theme.palette.grey[200]}`}
                bgcolor={theme.palette.common.white}
                overflow="hidden"
                minWidth="fit-content"
            >
                <ThemeProvider
                    theme={{
                        ...theme,

                        typography: {
                            ...theme.typography,
                            body1: {
                                ...theme.typography.body1,
                                color: theme.palette.common.black,
                            },
                        },
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                        <StaticDateRangePicker
                            dateRangeVariant="nights"
                            dateRange={value}
                            onChange={onChangeDateRange}
                            onMonthChange={(d: Dayjs | TZDate | null) => {
                                if (d !== null) {
                                    onChangeMonth(d as Dayjs | TZDate);
                                }
                            }}
                            minDate={props.minDate ?? TZDate.now().startOf('day')}
                            maxDate={TZDate.now().add(3, 'years').endOf('day')}
                            isDateUnavailable={(d: string) => !calendarAvailability.has(d)}
                            loading={false}
                            customDaySx={calendarDaySx}
                            renderCustomDay={expectPrice ? renderCustomDay : undefined}
                            showWeekNumbers={showWeekNumbers}
                            weekNumberRowHeight={50}
                            weekNumberRowMarginBottom={0}
                            weekNumberTopPadding={96}
                            slotProps={{
                                layout: {
                                    sx: calendarSx,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </ThemeProvider>
            </Stack>
        </Stack>
    );
}

function useCalendarAvailability(activities: ActivityInCalendar[], guests: GuestConfig[]) {
    return useMemo(() => {
        const roomGuests = guests
            .map((room) => room.adults + room.children.length)
            .toSorted((a, b) => b - a);

        const availabilityByDate = groupBy(activities, (product) =>
            product.start.format('YYYY-MM-DD'),
        );

        return Object.entries(availabilityByDate).reduce(
            (acc: Map<string, { products: ActivityInCalendar[] }>, [key, products]) => {
                const actualCapacityMap = new Map<string, number>();
                products.forEach((product) => {
                    actualCapacityMap.set(product.id, product.capacity);
                });
                const productsSortedByPersonsPerUnit = products.toSorted(
                    (a, b) => (b.personsPerUnit ?? -1) - (a.personsPerUnit ?? -1),
                );
                const doWeHaveEnoughRooms = roomGuests.every((guests) => {
                    const firstValidProduct = productsSortedByPersonsPerUnit.find(
                        (product) =>
                            (actualCapacityMap.get(product.id) === null ||
                                actualCapacityMap.get(product.id)! > 0) &&
                            (product.personsPerUnit === null || product.personsPerUnit >= guests),
                    );
                    if (firstValidProduct) {
                        actualCapacityMap.set(
                            firstValidProduct.id,
                            actualCapacityMap.get(firstValidProduct.id)! - 1,
                        );
                        return true;
                    }
                    return false;
                });

                if (!doWeHaveEnoughRooms) return acc;

                if (!acc.has(key)) {
                    acc.set(key, {
                        products: products,
                    });
                } else {
                    acc.set(key, {
                        products: [...(acc.get(key)?.products ?? []), ...products],
                    });
                }

                return acc;
            },
            new Map<string, { products: ActivityInCalendar[] }>(),
        );
    }, [activities, guests]);
}

function getLowestPriceFromAvailabilities(
    availabilities: { products: ActivityInCalendar[] },
    locale: string,
) {
    const prices = availabilities.products.map((product) => {
        return product.firstTicketOptionPrice ?? 0;
    });
    const lowest = Math.min(...prices);
    if (lowest === 0) return '-';
    return getLocaleNumberFormatNoDecimals(locale, lowest);
}

function useCalendarSx() {
    const customizations = useCustomizations();
    return useMemo(
        () => ({
            calendarSx: {
                minWidth: 380,
                [`@container accommodationsearchform (width < ${395}px)`]: {
                    minWidth: 330,
                },
                [`@container accommodationsearchform (width < ${
                    360 - customizations.baseUnit * 4
                }px)`]: {
                    minWidth: 300,
                },
                '& .MuiDayCalendar-header': {
                    minWidth: 380,
                    padding: 0,
                    maxHeight: 'auto',
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        minWidth: 330,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        minWidth: 300,
                    },
                },
                '& .MuiDayCalendar-weekDayLabel': {
                    minWidth: 48,
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        minWidth: 42,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        minWidth: 36,
                    },
                },
                '& .MuiDayCalendar-slideTransition': {
                    minHeight: 350,
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        minHeight: 310,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        minHeight: 280,
                    },
                },
                '& .MuiDayCalendar-monthContainer': {
                    minWidth: 380,
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        minWidth: 330,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        minWidth: 300,
                    },
                },
                '& .MuiDateCalendar-root': {
                    minWidth: 380,
                    maxHeight: 'unset',
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        minWidth: 330,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        minWidth: 300,
                    },
                },
                '& .MuiDateCalendar-viewTransitionContainer': {
                    maxHeight: 'unset',
                },
                '& .MuiPickersDay-root': {
                    width: 50,
                    height: 50,
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        width: 42,
                        height: 42,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        width: 36,
                        height: 36,
                    },
                },
                // The outer flex item is `MuiDateRangePickerDay-root`, which
                // MUI v7 leaves at its default 54px for selected-range and
                // outside-current-month cells. Mixing 50px/54px children
                // inside `MuiDayCalendar-weekContainer` (display:flex,
                // justify-content:center) shifts partial rows off-center —
                // visible as the last row's first cell sliding ~10px left.
                // Force the wrapper to match the inner button.
                '& .MuiDateRangePickerDay-root': {
                    width: 50,
                    height: 50,
                    [`@container accommodationsearchform (width < ${395}px)`]: {
                        width: 42,
                        height: 42,
                    },
                    [`@container accommodationsearchform (width < ${
                        360 - customizations.baseUnit * 4
                    }px)`]: {
                        width: 36,
                        height: 36,
                    },
                },
            },
            calendarDaySx: {
                width: 50,
                height: 50,
                [`@container accommodationsearchform (width < ${395}px)`]: {
                    width: 42,
                    height: 42,
                },
                [`@container (width < ${360 - customizations.baseUnit * 4}px)`]: {
                    width: 'unset',
                    height: 'unset',
                },
            },
        }),
        [customizations],
    );
}
